import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { selectVersionsData } from '../../store/general/general-slice-selectors';
import { resetVersionSetsRestState } from '../../store/general/general-slice';
import {
  createVersionSet,
  deleteVersionSetById,
  patchVersionSetById,
} from '../../store/general/general-actions';
import { useAppDispatch, useAppSelector } from '../../store';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { isHttpError } from '@common/interfaces/api/http-error';
import TextInput from '@common/components/Inputs/TextInput';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { DeleteIcon, DownArrow, MoveRightArrow } from '@common/utils/icons/icons';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import VersionedPackage from '../../models/enums/versioned-package';
import VersionSet from '../../models/general/versions/version-set';
import BuildVersion from '../../models/enums/build-version';
import VersionsForm from './VersionsForm';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

const VersionSetForm: React.FC<{
  versionSet?: Partial<VersionSet>;
  suggestedSet?: Partial<VersionSet>;
  onClose: () => void;
  setBuildVersion: (version: string) => void;
  setEditedVersion: (newTag: string) => void;
}> = ({ versionSet, suggestedSet, onClose, setBuildVersion, setEditedVersion }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const versionsData = useAppSelector((state) => selectVersionsData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: versionsData.sets.rest.postError,
      patchError: versionsData.sets.rest.patchError,
      resetPostHandler: () =>
        dispatch(resetVersionSetsRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      resetPatchHandler: () =>
        dispatch(
          resetVersionSetsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null }),
        ),
    });

  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [buildAnchorEl, setBuildAnchorEl] = useState<HTMLButtonElement | null>(null);

  const {
    value: tag,
    validationResult: tagValidationResult,
    valueChangeHandler: tagChangeHandler,
  } = useValidatedInputEffect(
    versionSet?.tag ?? '',
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('tag'),
    removeServerError,
    resetToggle,
  );

  useEffect(() => {
    if (
      (versionsData.sets.rest.postStatus === HttpFetchStatus.Success ||
        versionsData.sets.rest.patchStatus === HttpFetchStatus.Success ||
        versionsData.sets.rest.deleteStatus === HttpFetchStatus.Success) &&
      versionSet != null
    ) {
      if (versionsData.sets.rest.postStatus === HttpFetchStatus.Success) {
        setBuildVersion(tag);
      }
      updateResetToggle();
      onClose();
    }
  }, [
    onClose,
    versionsData.sets.rest.patchStatus,
    versionsData.sets.rest.postStatus,
    updateResetToggle,
    versionsData.sets.rest.deleteStatus,
    versionSet,
    setBuildVersion,
    tag,
  ]);

  const {
    value: roboticsSoftwareId,
    validationResult: roboticsSoftwareIdValidationResult,
    valueChangeHandler: roboticsSoftwareIdChangeHandler,
    setValue: roboticsSoftwareIdSetValue,
  } = useValidatedSelectEffect(
    suggestedSet?.roboticsSoftwareId ?? versionSet?.roboticsSoftwareId ?? '',
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('roboticsSoftwareId'),
    removeServerError,
    resetToggle,
  );

  const {
    value: debianMetaPackageId,
    validationResult: debianMetaPackageIdValidationResult,
    valueChangeHandler: debianMetaPackageIdChangeHandler,
    setValue: debianMetaPackageIdSetValue,
  } = useValidatedSelectEffect(
    suggestedSet?.debianMetaPackageId ?? versionSet?.debianMetaPackageId ?? '',
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('debianMetaPackageId'),
    removeServerError,
    resetToggle,
  );

  const {
    value: managementContainersId,
    validationResult: managementContainersIdValidationResult,
    valueChangeHandler: managementContainersIdChangeHandler,
    setValue: managementContainersIdSetValue,
  } = useValidatedSelectEffect(
    suggestedSet?.managementContainersId ?? versionSet?.managementContainersId ?? '',
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('managementContainersId'),
    removeServerError,
    resetToggle,
  );

  const closeHandler = () => {
    dispatch(
      resetVersionSetsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
    updateResetToggle();
    onClose();
  };

  const buildPayload = () => ({
    tag,
    roboticsSoftwareId,
    debianMetaPackageId,
    managementContainersId,
  });

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (versionSet?.id != null) {
      dispatch(
        patchVersionSetById({
          id: versionSet.id,
          data: buildPayload(),
        }),
      );
    } else {
      dispatch(createVersionSet(buildPayload()));
    }
  };

  const openConfirmDeleteDialogClickHandler = () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setIsConfirmDeleteDialogOpen(false);
  };

  const deleteVersionSetClickHandler = () => {
    if (versionSet?.id != null) {
      dispatch(deleteVersionSetById(versionSet.id));
      setIsConfirmDeleteDialogOpen(false);
    }
  };

  const closeBuildPopoverHandler = () => {
    setBuildAnchorEl(null);
  };

  const openBuildPopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBuildAnchorEl(event.currentTarget);
  };

  const changeCopyBranchClickHandler = (newTag: string) => {
    closeBuildPopoverHandler();
    setEditedVersion(newTag);
  };

  const isMainTag = Object.values(BuildVersion).some((bv) => bv === tag);

  return (
    <React.Fragment>
      <Dialog open={versionSet != null} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={formSubmitHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {suggestedSet != null ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="inherit">
                  {suggestedSet.tag != null
                    ? t('title-copy-versions-from-a-to-b')
                    : t('title-update-with-latest')}
                </Typography>
                <Typography variant="inherit">
                  {Object.values(BuildVersion).some((bv) => bv === suggestedSet.tag)
                    ? t('build-version-' + suggestedSet.tag)
                    : suggestedSet.tag}
                </Typography>
                <MoveRightArrow />
                <Button
                  size="large"
                  sx={{ flexGrow: 1 }}
                  endIcon={<DownArrow />}
                  onClick={openBuildPopoverClickHandler}
                >
                  {versionSet?.tag}
                </Button>
              </Stack>
            ) : versionSet?.id != null ? (
              t('title-update-version')
            ) : (
              t('title-create-version')
            )}
            {isMainTag && suggestedSet == null && ` (${t('build-version-' + tag)})`}
          </DialogTitle>
          <DialogContent>
            <Stack mt={2} spacing={2}>
              {versionsData.sets.rest.postError !== null &&
                isHttpError(versionsData.sets.rest.postError) && (
                  <CustomAlert
                    severity="error"
                    header={t(versionsData.sets.rest.postError.message)}
                  />
                )}
              {versionsData.sets.rest.patchError !== null &&
                isHttpError(versionsData.sets.rest.patchError) && (
                  <CustomAlert
                    severity="error"
                    header={t(versionsData.sets.rest.patchError.message)}
                  />
                )}
              {!isMainTag && suggestedSet == null && (
                <TextInput
                  value={tag}
                  onChange={tagChangeHandler}
                  validationResult={tagValidationResult}
                  label={t('input-label-version-set-tag')}
                />
              )}

              <VersionsForm
                versionPackage={VersionedPackage.ROBOTICS_SOFTWARE}
                label={t('input-label-version-robotics-software')}
                initialValueId={
                  suggestedSet != null ? (versionSet?.roboticsSoftwareId ?? undefined) : undefined
                }
                value={roboticsSoftwareId}
                onChange={roboticsSoftwareIdChangeHandler}
                validationResult={roboticsSoftwareIdValidationResult}
                setValue={roboticsSoftwareIdSetValue}
              />

              <VersionsForm
                versionPackage={VersionedPackage.DEBIAN_META_PACKAGE}
                label={t('input-label-version-debian-meta-package')}
                initialValueId={
                  suggestedSet != null ? (versionSet?.debianMetaPackageId ?? undefined) : undefined
                }
                value={debianMetaPackageId}
                onChange={debianMetaPackageIdChangeHandler}
                validationResult={debianMetaPackageIdValidationResult}
                setValue={debianMetaPackageIdSetValue}
              />

              <VersionsForm
                versionPackage={VersionedPackage.MANAGEMENT_CONTAINERS}
                label={t('input-label-version-management-containers')}
                initialValueId={
                  suggestedSet != null
                    ? (versionSet?.managementContainersId ?? undefined)
                    : undefined
                }
                value={managementContainersId}
                onChange={managementContainersIdChangeHandler}
                validationResult={managementContainersIdValidationResult}
                setValue={managementContainersIdSetValue}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                variant="contained"
                data-testid="button-add"
                disabled={
                  versionsData.sets.rest.postStatus === HttpFetchStatus.Loading ||
                  versionsData.sets.rest.patchStatus === HttpFetchStatus.Loading
                }
                type="submit"
              >
                {versionsData.sets.rest.postStatus === HttpFetchStatus.Loading ||
                versionsData.sets.rest.patchStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : versionSet?.id == null ? (
                  t('button-add-version')
                ) : (
                  t('button-save')
                )}
              </Button>
              {versionSet?.id != null && suggestedSet == null && (
                <TooltipIconButton
                  icon={<DeleteIcon color="error" data-testid="delete-version-button" />}
                  onClick={openConfirmDeleteDialogClickHandler}
                  tooltip={t('info-tooltip-delete')}
                />
              )}
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomConfirmAbortDialog
        open={isConfirmDeleteDialogOpen}
        title={t('title-delete-version-set')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteVersionSetClickHandler}
      />
      {versionsData.sets.data != null && (
        <Popover
          id="merge-build-version-popover-id"
          open={Boolean(buildAnchorEl)}
          anchorEl={buildAnchorEl}
          onClose={closeBuildPopoverHandler}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {versionsData.sets.data
            .filter((vs) => vs.tag !== suggestedSet?.tag)
            .map((vs) => (
              <MenuItem
                key={vs.id}
                value={vs.tag}
                onClick={() => changeCopyBranchClickHandler(vs.tag)}
              >
                {Object.values(BuildVersion).some((bv) => bv === vs.tag)
                  ? t('build-version-' + vs.tag)
                  : vs.tag}
              </MenuItem>
            ))}
        </Popover>
      )}
    </React.Fragment>
  );
};

export default VersionSetForm;
